import React from "react";
import {
	Badge,
	Box,
	Button,
	ButtonDropdown,
	DateRangePicker,
	Header,
	FormField,
	Input,
	Link,
	Pagination,
	Select,
	SpaceBetween,
	StatusIndicator,
	Table,
} from "@cloudscape-design/components";

const EVENT_OPTIONS = [
	{ label: "All events", value: "all" },
	{ label: "Registration", value: "registration" },
	{ label: "FTD", value: "ftd" },
	{ label: "Unknown", value: "unknown" },
];

const DATE_RANGE_I18N = {
	todayAriaLabel: "Today",
	nextMonthAriaLabel: "Next month",
	previousMonthAriaLabel: "Previous month",
	customRelativeRangeDurationLabel: "Duration",
	customRelativeRangeDurationPlaceholder: "Enter duration",
	customRelativeRangeOptionLabel: "Custom range",
	customRelativeRangeOptionDescription: "Set a custom range in the past",
	customRelativeRangeUnitLabel: "Unit",
	formatRelativeRange: (value) => value,
	formatUnit: (unit, value) => `${value} ${unit}`,
	dateTimeConstraintText: "Use YYYY/MM/DD.",
	relativeModeTitle: "Relative range",
	absoluteModeTitle: "Absolute range",
	relativeRangeSelectionHeading: "Choose a range",
	startDateLabel: "Start date",
	endDateLabel: "End date",
	startTimeLabel: "Start time",
	endTimeLabel: "End time",
	clearButtonLabel: "Clear",
	cancelButtonLabel: "Cancel",
	applyButtonLabel: "Apply",
};

const getErrorText = (error) => error?.text || error?.message || String(error || "Unknown error");

const formatDate = (value) => {
	if (!value) return "-";
	return String(value).replace("T", " ").replace(".000Z", "").slice(0, 19);
};

const eventBadge = (eventType, eventSource) => {
	const suffix = eventSource === "url" ? " (detected)" : "";
	if (eventType === "registration") return <Badge color="blue">Registration{suffix}</Badge>;
	if (eventType === "ftd") return <Badge color="green">FTD{suffix}</Badge>;
	return <Badge color="grey">Unknown</Badge>;
};

const copyText = async (text) => {
	const value = String(text || "");
	if (navigator.clipboard && window.isSecureContext) {
		await navigator.clipboard.writeText(value);
		return;
	}

	const input = document.createElement("textarea");
	input.value = value;
	input.style.position = "fixed";
	input.style.opacity = "0";
	document.body.appendChild(input);
	input.select();
	document.execCommand("copy");
	document.body.removeChild(input);
};

export default class Postbacks extends React.Component {
	listRequestId = 0;

	state = {
		items: [],
		loading: true,
		exporting: false,
		error: "",
		page: 1,
		limit: 25,
		total: 0,
		pages: 1,
		eventOption: EVENT_OPTIONS[0],
		dateRange: null,
		orderDirection: "DESC",
		clickIdDraft: "",
		buyerIdDraft: "",
		partnerIdDraft: "",
		clickId: "",
		buyerId: "",
		partnerId: "",
	};

	componentDidMount() {
		this.load();
	}

	componentWillUnmount() {
		this.listRequestId += 1;
	}

	getFilters = (eventType = this.state.eventOption.value) => {
		const filters = {
			page: this.state.page,
			limit: this.state.limit,
			eventType,
			orderDirection: this.state.orderDirection,
		};

		if (this.state.dateRange?.type === "absolute") {
			filters.dateFrom = this.state.dateRange.startDate;
			filters.dateTo = this.state.dateRange.endDate;
		}
		if (this.state.clickId) filters.clickId = this.state.clickId;
		if (this.state.buyerId) filters.buyerId = this.state.buyerId;
		if (this.state.partnerId) filters.partnerId = this.state.partnerId;

		return filters;
	};

	load = () => {
		const requestId = ++this.listRequestId;
		this.setState({ loading: true, error: "" });
		this.props.Interface.request("get", { class: "data", module: "postbacks", method: "list" }, this.getFilters())
			.then((response) => {
				if (requestId !== this.listRequestId) return;
				this.setState({
					items: response.items || [],
					page: response.pagination?.page || 1,
					limit: response.pagination?.limit || 25,
					total: response.pagination?.total || 0,
					pages: response.pagination?.pages || 1,
					loading: false,
				});
			})
			.catch((error) => {
				if (requestId === this.listRequestId) this.setState({ loading: false, error: getErrorText(error) });
			});
	};

	applyFilter = (nextState) => {
		this.setState({ ...nextState, page: 1 }, this.load);
	};

	applySearchFilters = () => {
		const partnerId = this.state.partnerIdDraft.trim();
		if (partnerId && (!/^\d+$/.test(partnerId) || !Number.isSafeInteger(Number(partnerId)) || Number(partnerId) < 1)) {
			this.setState({ error: "Partner UID must be a positive integer." });
			return;
		}

		this.setState(
			{
				clickId: this.state.clickIdDraft.trim(),
				buyerId: this.state.buyerIdDraft.trim(),
				partnerId,
				page: 1,
				error: "",
			},
			this.load,
		);
	};

	clearFilters = () => {
		this.setState(
			{
				dateRange: null,
				eventOption: EVENT_OPTIONS[0],
				clickIdDraft: "",
				buyerIdDraft: "",
				partnerIdDraft: "",
				clickId: "",
				buyerId: "",
				partnerId: "",
				page: 1,
				error: "",
			},
			this.load,
		);
	};

	exportCsv = (eventType) => {
		this.setState({ exporting: true, error: "" });
		this.props.Interface.request("get", { class: "data", module: "postbacks", method: "export" }, this.getFilters(eventType))
			.then((response) => {
				const blob = new Blob([response.csv], { type: "text/csv;charset=utf-8;" });
				const link = document.createElement("a");
				link.href = URL.createObjectURL(blob);
				link.download = response.fileName || `postbacks-${eventType}.csv`;
				link.click();
				URL.revokeObjectURL(link.href);
				this.setState({ exporting: false });
			})
			.catch((error) => this.setState({ exporting: false, error: getErrorText(error) }));
	};

	openProfile = (uid) => {
		if (uid !== null && typeof uid !== "undefined") this.props.Interface.loadInfo(Number(uid));
	};

	render() {
		return (
			<SpaceBetween size="m">
				{this.state.error && <StatusIndicator type="error">{this.state.error}</StatusIndicator>}
				<Table
					stickyHeader
					trackBy="id"
					loading={this.state.loading}
					loadingText="Loading postbacks"
					items={this.state.items}
					sortingColumn={{ sortingField: "date" }}
					sortingDescending={this.state.orderDirection === "DESC"}
					onSortingChange={({ detail }) => this.applyFilter({ orderDirection: detail.isDescending ? "DESC" : "ASC" })}
					columnDefinitions={[
						{ id: "date", header: "Date", sortingField: "date", cell: (item) => formatDate(item.date), minWidth: 165 },
						{ id: "event", header: "Event", cell: (item) => eventBadge(item.eventType, item.eventSource), minWidth: 160 },
						{
							id: "status",
							header: "Status",
							cell: (item) => <StatusIndicator type={item.status === "success" ? "success" : item.status === "failed" ? "error" : "stopped"}>{item.status || "-"}</StatusIndicator>,
						},
						{
							id: "partnerID",
							header: "Partner ID",
							cell: (item) => (item.partnerID ? <Link onFollow={(event) => { event.preventDefault(); this.openProfile(item.partnerID); }}>{item.partnerID}</Link> : "-"),
						},
						{
							id: "userID",
							header: "User ID",
							cell: (item) => <Link onFollow={(event) => { event.preventDefault(); this.openProfile(item.userID); }}>{item.userID}</Link>,
						},
						{
							id: "clickID",
							header: "Click ID",
							cell: (item) => item.clickID || "-",
							minWidth: 220,
						},
						{ id: "buyerID", header: "Buyer ID", cell: (item) => item.buyerID || "-", minWidth: 220 },
						{
							id: "request",
							header: "Request",
							cell: (item) => <div title={item.request} style={{ maxWidth: 520, overflowWrap: "anywhere" }}>{item.request}</div>,
							minWidth: 320,
						},
						{
							id: "copy",
							header: "Copy",
							cell: (item) => <Button iconName="copy" variant="icon" ariaLabel="Copy request" onClick={() => copyText(item.request).catch((error) => this.setState({ error: getErrorText(error) }))} />,
						},
					]}
					filter={
						<SpaceBetween direction="horizontal" size="s">
							<FormField label="Date">
								<div style={{ minWidth: 220 }}>
									<DateRangePicker
										value={this.state.dateRange}
										onChange={({ detail }) => this.applyFilter({ dateRange: detail.value })}
										relativeOptions={[]}
										dateOnly
										placeholder="Filter by date"
										i18nStrings={DATE_RANGE_I18N}
										isValidRange={(range) => ({ valid: range.type !== "absolute" || range.startDate <= range.endDate, errorMessage: "Start date must be before end date." })}
									/>
								</div>
							</FormField>
							<FormField label="Event">
								<Select
									selectedOption={this.state.eventOption}
									onChange={({ detail }) => this.applyFilter({ eventOption: detail.selectedOption })}
									options={EVENT_OPTIONS}
								/>
							</FormField>
							<FormField label="Partner UID">
								<Input
									value={this.state.partnerIdDraft}
									inputMode="numeric"
									placeholder="Exact UID"
									onChange={({ detail }) => this.setState({ partnerIdDraft: detail.value })}
								/>
							</FormField>
							<FormField label="Click ID">
								<Input
									value={this.state.clickIdDraft}
									placeholder="Exact Click ID"
									onChange={({ detail }) => this.setState({ clickIdDraft: detail.value })}
								/>
							</FormField>
							<FormField label="Buyer ID">
								<Input
									value={this.state.buyerIdDraft}
									placeholder="Exact Buyer ID"
									onChange={({ detail }) => this.setState({ buyerIdDraft: detail.value })}
								/>
							</FormField>
							<div style={{ paddingTop: 24 }}>
								<SpaceBetween direction="horizontal" size="xs">
									<Button variant="primary" onClick={this.applySearchFilters}>Apply</Button>
									<Button onClick={this.clearFilters}>Clear</Button>
								</SpaceBetween>
							</div>
						</SpaceBetween>
					}
					header={
						<Header
							counter={`(${this.state.total})`}
							actions={
								<SpaceBetween direction="horizontal" size="s">
									<Button iconName="refresh" onClick={this.load}>Refresh</Button>
									<ButtonDropdown
										loading={this.state.exporting}
										items={[
											{ id: "all", text: "All events" },
											{ id: "registration", text: "Registration" },
											{ id: "ftd", text: "FTD" },
										]}
										onItemClick={({ detail }) => this.exportCsv(detail.id)}>
										Export CSV
									</ButtonDropdown>
								</SpaceBetween>
							}>
							Postbacks
						</Header>
					}
					pagination={
						<Pagination
							currentPageIndex={this.state.page}
							pagesCount={this.state.pages}
							onChange={({ detail }) => this.setState({ page: detail.currentPageIndex }, this.load)}
						/>
					}
					empty={<Box textAlign="center">No postbacks found</Box>}
				/>
			</SpaceBetween>
		);
	}
}
